import { Cart, GPS, nativeAppHeaders } from '@ynap/api-utils'
import { delay } from '@ynap/promise-utils'
import { SET_PAYMENT_METHOD_REQUEST, SET_PAYMENT_METHOD_SUCCESS, SET_PAYMENT_METHOD_ERROR } from '../constants/ActionTypes'
import cardValidator from 'card-validator'
import { getWatsonConfig, getPaymentConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getCurrentShippingAddress } from '../selectors/getShippingAddressStep'
import { getCurrentBillingAddress, getSavedCardById } from '../selectors/getPaymentStep'
import { reconcileAddress } from './setShippingAddress'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'
import { validatorToGPS, validatorToVault } from './common/mapCardType'
import { checkRectificationOnError, checkRectificationOnSuccess } from '../actions/cartRectification'
import { checkGuestIdentityError } from './checkGuestIdentityError'
import { checkoutError, submitReduxForm } from './checkout'
import { formName as dduFormName } from '../../../components/DDUAcceptanceForm/DDUAcceptanceForm'
import { getLocale } from '@ynap/cart-state'
import { collectBrowserInfo } from '../../../adyen'
import { getCardholderName } from '../utils/paymentUtils'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'

export default function setPaymentMethod(values, redirect) {
    return async (dispatch, getState, fetch) => {
        const state = getState()

        const watsonConfig = getWatsonConfig(state)
        const currentShippingAddress = getCurrentShippingAddress(state)
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const globalFetchJSON = wrapFetchWithJSON(window.fetch, getTimeout(state))
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        try {
            submitReduxForm(dduFormName, dispatch, state)
        } catch (e) {
            return null
        }

        //you must use 127.0.0.1 for local dev else paypal will not work as the return url cannot use localhost
        const { returnUrl, paymentFromCart } = values
        const cancelUrl = returnUrl

        dispatch(setPaymentMethodRequest())

        // TODO: have the user sumary saved in state sometime earlier in checkout flow.
        const user = new Promise((resolve) => resolve(window.SF && window.SF.getUserSummary()))
        const isTransient = user.then((user) => !user)
        const browserInfo = collectBrowserInfo()
        let promise
        const currentBillingAddress = getCurrentBillingAddress(state)

        if (values.paymentMethodId === 'CREDIT_CARD') {
            const paymentConfig = getPaymentConfig(state)
            promise = isTransient.then((isTransient) =>
                reconcileAddress(fetchJSON, values.billingAddress, isTransient, undefined, currentBillingAddress || currentShippingAddress, watsonConfig).then(
                    (billingAddress) =>
                        globalFetchJSON(addCreditCard(values.creditCard, paymentConfig)).then(({ cardId }) =>
                            fetchJSON(
                                Cart.setPaymentInstruction({
                                    paymentInstruction: [
                                        {
                                            payMethodId: values.paymentMethodId,
                                            billing_address_id: billingAddress.addressId,
                                            ...(paymentConfig.paymentSystem === 'GPS' && { apiToken: cardId, ...mapCardToWCS(values.creditCard) }),
                                            cc_cvv_entered: 'true',
                                            primary: values.creditCard.saveCard,
                                            returnUrl: returnUrl,
                                            cancelUrl: cancelUrl,
                                            saveCard: values.creditCard.saveCard,
                                            ...browserInfo,
                                            ...(paymentFromCart && { paymentFromCart }),
                                        },
                                    ],
                                    ...watsonConfig,
                                    langId,
                                    additionalHeaders,
                                })
                            )
                        )
                )
            )
        } else if (values.paymentMethodId && values.paymentMethodId.indexOf('CREDIT_CARD/') === 0) {
            const [paymentMethodId, cardId] = values.paymentMethodId.split('/')
            const savedCard = getSavedCardById(state, cardId)

            promise = isTransient.then((isTransient) =>
                reconcileAddress(fetchJSON, values.billingAddress, isTransient, currentBillingAddress || currentShippingAddress, watsonConfig).then(
                    async (billingAddress) => {
                        return fetchJSON(
                            Cart.setPaymentInstruction({
                                paymentInstruction: [
                                    {
                                        payMethodId: paymentMethodId,
                                        billing_address_id: billingAddress.addressId,
                                        apiToken: savedCard.cardId,
                                        cc_type: savedCard.type,
                                        cc_expiryMonth: savedCard.expireMonth,
                                        cc_expiryYear: savedCard.expireYear.length <= 2 ? `20${savedCard.expireYear}` : savedCard.expireYear,
                                        cc_lastFourDigits: savedCard.lastFourDigits,
                                        cc_cvv_entered: savedCard.saved ? undefined : 'true',
                                        cc_cardHolderName: getCardholderName(savedCard, billingAddress),
                                        returnUrl: returnUrl,
                                        cancelUrl: cancelUrl,
                                        ...browserInfo,
                                        ...(paymentFromCart && { paymentFromCart }),
                                    },
                                ],
                                ...watsonConfig,
                                langId,
                                additionalHeaders,
                            })
                        )
                    }
                )
            )
        } else if (values.paymentMethodId === 'COD') {
            promise = isTransient.then((isTransient) =>
                reconcileAddress(fetchJSON, values.billingAddress, isTransient, currentBillingAddress || currentShippingAddress, watsonConfig).then(
                    async (billingAddress) => {
                        return fetchJSON(
                            Cart.setPaymentInstruction({
                                paymentInstruction: [
                                    {
                                        payMethodId: values.paymentMethodId,
                                        billing_address_id: billingAddress.addressId,
                                        ...(paymentFromCart && { paymentFromCart }),
                                    },
                                ],
                                ...watsonConfig,
                                langId,
                                additionalHeaders,
                            })
                        )
                    }
                )
            )
        } else if (
            values.paymentMethodId.indexOf('PAYPAL') === 0 ||
            values.paymentMethodId.match(/^(APPLEPAY|PAYPAL|KLARNA_PAY_NOW|KLARNA_PAY_LATER|KLARNA_SLICE_IT|KLARNA|AFFIRM)$/)
        ) {
            const [paymentMethodId, paypalBillingAgreement] = values.paymentMethodId.split('/')
            promise = isTransient.then((isTransient) =>
                reconcileAddress(fetchJSON, values.billingAddress, isTransient, currentBillingAddress || currentShippingAddress, watsonConfig).then(
                    async (billingAddress) => {
                        return fetchJSON(
                            Cart.setPaymentInstruction({
                                paymentInstruction: [
                                    {
                                        payMethodId: paymentMethodId,
                                        billing_address_id: billingAddress.addressId,
                                        returnUrl: returnUrl,
                                        cancelUrl: cancelUrl,
                                        ...(values.paymentMethodId === 'PAYPAL' ? { savePayment: values.savePayment } : {}),
                                        ...(paypalBillingAgreement ? { paypalBillingAgreement } : {}),
                                        ...(paymentFromCart && { paymentFromCart }),
                                    },
                                ],
                                ...watsonConfig,
                                langId,
                                additionalHeaders,
                            })
                        )
                    }
                )
            )
        } else if (values.paymentMethodId === 'STORE_CREDIT') {
            promise = isTransient.then((isTransient) =>
                reconcileAddress(fetchJSON, values.billingAddress, isTransient, currentBillingAddress || currentShippingAddress, watsonConfig).then(
                    async (billingAddress) => {
                        return fetchJSON(
                            Cart.setPaymentInstruction({
                                paymentInstruction: [
                                    {
                                        payMethodId: values.paymentMethodId,
                                        billing_address_id: billingAddress.addressId,
                                        ...(paymentFromCart && { paymentFromCart }),
                                    },
                                ],
                                clearExisting: false,
                                ...watsonConfig,
                                langId,
                                additionalHeaders,
                            })
                        )
                    }
                )
            )
        } else {
            throw new Error(`Invalid payment method "${values.paymentMethodId}" selected`)
        }

        try {
            const data = await delay(promise, getDelay(state))
            dispatch(setPaymentMethodSuccess(data))
            checkRectificationOnSuccess(dispatch, data, redirect)
            return data
        } catch (error) {
            if (error.response) {
                try {
                    const json = await error.response.clone().json()
                    const jsonError = json.errors[0] && json.errors[0]
                    if (jsonError) {
                        dispatch(checkoutError(jsonError))
                    }
                } catch (e) {
                    /* don't do anything here, apparently? */
                }
            } else {
                dispatch(setPaymentMethodError(error))
            }

            checkRectificationOnError(error, dispatch)
            checkGuestIdentityError(error, dispatch, state)
            throw error
        }
    }
}

export function setPaymentMethodRequest() {
    return {
        type: SET_PAYMENT_METHOD_REQUEST,
    }
}

export function setPaymentMethodSuccess(data) {
    return {
        type: SET_PAYMENT_METHOD_SUCCESS,
        payload: data,
    }
}

export function setPaymentMethodError(error) {
    return {
        type: SET_PAYMENT_METHOD_ERROR,
        error,
    }
}

// Adds credit card to GPS
export function addCreditCard(creditCard, options = {}) {
    return GPS.addCreditCard({
        ...options,
        ...mapCardToGPS(creditCard),
    })
}

export function mapCardToWCS(values) {
    const card = mapCardToGPS(values)
    return {
        cc_cardHolderName: card.cardHolderName,
        cc_type: card.cardType,
        cc_lastFourDigits: `${card.cardNumber}`.substr(-4),
        cc_expiryYear: `${card.expiryYear}`,
        cc_expiryMonth: card.expiryMonth < 10 ? `0${card.expiryMonth}` : `${card.expiryMonth}`,
    }
}

export function mapCardToGPS(values = {}) {
    const expiryYear = values?.expiryDateStr
        ? `20${values.expiryDateStr.slice(-2)}`
        : values.expiryDate.year.length <= 2
        ? `20${values.expiryDate.year}`
        : values.expiryDate.year
    const expiryMonth = values?.expiryDateStr ? values.expiryDateStr.substring(0, 2) : values.expiryDate.month

    return {
        cardHolderName: values.cardHolderName.trim(),
        cardNumber: values.number?.replace(/\D/g, ''),
        cardType: validatorToGPS(cardValidator.number(values.number).card.type),
        cvsNumber: values.cvv,
        expiryYear: parseInt(expiryYear, 10),
        expiryMonth: parseInt(expiryMonth, 10),
        keepCard: values.saveCard === 'true',
    }
}

export function mapCardToVault(values = {}, address = {}) {
    return {
        card: {
            number: values.number.replace(/\D/g, ''),
            code: validatorToVault(cardValidator.number(values.number).card.type),
            expire_year: values.expiryDate.year.length <= 2 ? `20${values.expiryDate.year}` : values.expiryDate.year,
            expire_month: values.expiryDate.month,
        },
        card_holder: {
            first_name: address.firstName,
            last_name: address.lastName,
            country: address.country,
            province: address.state,
            city: address.city ? address.city : address.country,
            postal_code: address.zipCode,
            address: address.addressLine && address.addressLine.filter((addressLine) => addressLine).join(', '),
        },
        cc_type: validatorToVault(cardValidator.number(values.number).card.type),
    }
}
